import {
  advanceCheckpoint,
  advanceInterlockState,
  createCheckpointState,
  currentCheckpointColor,
  FRESH_TIRE_HEALTH,
  getVehicleGeometry,
  hashPhysicsConfig,
  REPLAY_SCHEMA_VERSION,
  resolveControls,
  stepVehicle,
  type CheckpointState,
  type InterlockState,
  type VehicleState,
  type SectorTicks,
  type PhysicsCarConfig,
} from '@cars/shared';
import type { TrackRuntime } from './TrackRuntime';
import type { CarInputState, CarTelemetry, DriveDirection, ResolvedCarControls } from '../types';
import type { LapTimer } from '../../utils/LapTimer';

export interface CarStepResult {
  lapFinished: boolean;
  lapTimeMs?: number;
  checkpointIndex?: number;
  controls?: ResolvedCarControls;
  state?: VehicleState;
  sectorIndex?: 0 | 1 | 2;
  sectorTicks?: number;
  lapSectorTicks?: SectorTicks;
  pitServiceVisit?: boolean;
  pitServiceActive?: boolean;
  pitSpeedLimitActive?: boolean;
}

export type VehicleTrack = Pick<
  TrackRuntime,
  'getPhysicsConfig' | 'getSampler' | 'getMapHash' | 'getCheckpointColorAt'
>;

export class VehicleRuntime {
  private timer: LapTimer | null = null;
  private previousInput: CarInputState = emptyInput();
  private driveDirection: DriveDirection = 0;
  private interlockState: InterlockState = {
    stoppedFromDirection: 0,
    reverseReleaseReady: true,
    forwardReleaseReady: true,
  };
  private currentState: VehicleState;
  private previousState: VehicleState;
  private checkpointState: CheckpointState = createCheckpointState();
  private telemetry: CarTelemetry;
  private readonly pathTrace: Array<{ x: number; y: number; tick: number }> = [];
  private invalidated = false;

  private readonly track: VehicleTrack;
  private readonly config: PhysicsCarConfig;

  constructor(
    x: number,
    y: number,
    rotation: number,
    track: VehicleTrack,
    config: PhysicsCarConfig,
  ) {
    this.track = track;
    this.config = config;
    // Preserve Phaser DegToRad operation order, including raw 180-degree spawns.
    const headingRad = rotation * (Math.PI / 180);
    this.currentState = initialState(x, y, headingRad);
    this.previousState = cloneState(this.currentState);
    this.telemetry = emptyTelemetry();
  }

  setTimer(timer: LapTimer): void {
    this.timer = timer;
  }

  stepOnce(stepMs: number, input: CarInputState, tick: number): CarStepResult {
    if (this.invalidated) return { lapFinished: false };
    const controls = resolveControls(input, {
      driveDirection: this.driveDirection,
      previousInput: this.previousInput,
      ...this.interlockState,
    });
    return this.applyResolvedControls(stepMs, controls, tick, input);
  }

  stepReplayOnce(stepMs: number, controls: ResolvedCarControls, tick: number): CarStepResult {
    if (this.invalidated) return { lapFinished: false };
    return this.applyResolvedControls(stepMs, controls, tick, emptyInput());
  }

  restoreState(state: VehicleState): void {
    this.currentState = cloneState(state);
    this.previousState = cloneState(state);
    this.pathTrace.length = 0;
    this.driveDirection = directionForState(state);
    this.telemetry = {
      ...emptyTelemetry(),
      speedRatio: Math.min(
        1,
        Math.hypot(state.longitudinalSpeed, state.lateralSpeed) /
          Math.max(this.config.maxSpeed * this.track.getPhysicsConfig().trackMaxSpeed, 0.001),
      ),
      driveDirection: this.driveDirection,
      lateralSpeed: state.lateralSpeed,
      yawRate: state.yawRate,
      steeringAngleRad: state.steeringAngleRad,
    };
    this.interlockState = {
      stoppedFromDirection: 0,
      reverseReleaseReady: true,
      forwardReleaseReady: true,
    };
    this.previousInput = emptyInput();
    this.invalidated = false;
  }

  resetCheckpointState(): void {
    this.checkpointState = createCheckpointState();
    this.timer?.reset();
  }

  private applyResolvedControls(
    stepMs: number,
    controls: ResolvedCarControls,
    tick: number,
    input: CarInputState,
  ): CarStepResult {
    const result = stepVehicle({
      state: this.currentState,
      controls,
      car: this.config,
      track: this.track.getPhysicsConfig(),
      sampler: this.track.getSampler(),
      stepMs,
      tick,
    });
    this.previousState = cloneState(this.currentState);
    this.currentState = result.state;
    this.pathTrace.push({ ...this.currentState.position, tick });
    if (this.pathTrace.length > 2048) this.pathTrace.shift();
    const previousDirection = this.driveDirection;
    this.driveDirection = directionForState(this.currentState);
    this.interlockState = advanceInterlockState(this.interlockState, {
      previousDriveDirection: previousDirection,
      driveDirection: this.driveDirection,
      input,
    });
    const checkpointResult = this.updateCheckpointState(tick);
    this.telemetry = {
      appliedThrottle: result.telemetry.appliedThrottle,
      appliedBrake: result.telemetry.appliedBrake,
      appliedSteering: result.telemetry.appliedSteering,
      speedRatio: result.telemetry.speedRatio,
      driveDirection: this.driveDirection,
      tick,
      lateralSpeed: result.telemetry.lateralSpeed,
      yawRate: result.telemetry.yawRate,
      steeringAngleRad: result.telemetry.steeringAngleRad,
      ...(result.telemetry.frontGripMultiplier !== undefined
        ? {
            frontGripMultiplier: result.telemetry.frontGripMultiplier,
            rearGripMultiplier: result.telemetry.rearGripMultiplier,
            frontSlipAngle: result.telemetry.frontSlipAngle,
            rearSlipAngle: result.telemetry.rearSlipAngle,
            frontSaturationRatio: result.telemetry.frontSaturationRatio,
            rearSaturationRatio: result.telemetry.rearSaturationRatio,
          }
        : {}),
      ...(result.telemetry.tireWear ? { tireWear: { ...result.telemetry.tireWear } } : {}),
      pitServiceActive: result.telemetry.pitServiceActive,
      pitSpeedLimitActive: result.telemetry.pitSpeedLimitActive,
      pitServiceVisit: result.telemetry.pitServiceVisit,
      pitStopVisitCount: result.telemetry.pitStopVisitCount,
      pitLimiterApplied: result.telemetry.pitLimiterApplied,
      wallHit: result.telemetry.wallHit,
    };
    this.previousInput = { ...input };
    return {
      ...checkpointResult,
      controls,
      state: this.getState(),
      pitServiceVisit: result.telemetry.pitServiceVisit,
      pitServiceActive: result.telemetry.pitServiceActive,
      pitSpeedLimitActive: result.telemetry.pitSpeedLimitActive,
    };
  }

  getInterpolatedPose(alpha: number): { x: number; y: number; headingRad: number } {
    const clamped = Math.min(1, Math.max(0, alpha));
    const difference = normalizeAngle(this.currentState.headingRad - this.previousState.headingRad);
    return {
      x:
        this.previousState.position.x +
        (this.currentState.position.x - this.previousState.position.x) * clamped,
      y:
        this.previousState.position.y +
        (this.currentState.position.y - this.previousState.position.y) * clamped,
      headingRad: this.previousState.headingRad + difference * clamped,
    };
  }

  markAttemptInvalid(): void {
    this.invalidated = true;
  }

  isAttemptInvalid(): boolean {
    return this.invalidated;
  }

  getSpeedRatio(): number {
    return this.telemetry.speedRatio;
  }

  getTelemetry(): CarTelemetry {
    return {
      ...this.telemetry,
      wallHit: this.telemetry.wallHit
        ? { ...this.telemetry.wallHit, normal: { ...this.telemetry.wallHit.normal } }
        : undefined,
    };
  }

  getState(): VehicleState {
    return cloneState(this.currentState);
  }

  getMapHash(): string {
    return this.track.getMapHash();
  }

  getRunMetadata() {
    return {
      replaySchemaVersion: REPLAY_SCHEMA_VERSION,
      trackMapHash: this.track.getMapHash(),
      carConfigHash: hashPhysicsConfig(this.config),
    };
  }

  getPathTrace(): ReadonlyArray<{ x: number; y: number; tick: number }> {
    return this.pathTrace;
  }

  private updateCheckpointState(tick: number): {
    lapFinished: boolean;
    lapTimeMs?: number;
    checkpointIndex?: number;
    sectorIndex?: 0 | 1 | 2;
    sectorTicks?: number;
    lapSectorTicks?: SectorTicks;
  } {
    if (!this.timer) return { lapFinished: false };
    const geometry = getVehicleGeometry(this.currentState, this.config);
    const expectedColor = currentCheckpointColor(this.checkpointState);
    const checkpointColor = geometry.wheels.some(
      (wheel) => this.track.getCheckpointColorAt(wheel.x, wheel.y) === expectedColor,
    )
      ? expectedColor
      : null;
    const hitIndex = this.checkpointState.currentIndex;
    const transition = advanceCheckpoint(this.checkpointState, checkpointColor, tick);
    this.checkpointState = transition.state;
    const result = transition.result;
    if (!result.hit) return { lapFinished: false };
    const timerResult = this.timer.checkAtSimulationTick(tick);
    if (!result.lapFinished)
      return {
        lapFinished: false,
        checkpointIndex: hitIndex,
        ...(result.completedSectorIndex !== undefined
          ? {
              sectorIndex: result.completedSectorIndex,
              sectorTicks: result.completedSectorTicks,
            }
          : {}),
      };
    return {
      lapFinished: true,
      lapTimeMs: timerResult.lapTimeMs ?? (result.lapTicks ?? 0) * (1000 / 60),
      checkpointIndex: hitIndex,
      sectorIndex: result.completedSectorIndex,
      sectorTicks: result.completedSectorTicks,
      lapSectorTicks: result.completedLapSectorTicks,
    };
  }
}

function emptyInput(): CarInputState {
  return { accelerate: false, brake: false, turnLeft: false, turnRight: false };
}

function initialState(x: number, y: number, headingRad: number): VehicleState {
  return {
    position: { x, y },
    headingRad,
    longitudinalSpeed: 0,
    lateralSpeed: 0,
    yawRate: 0,
    steeringAngleRad: 0,
    tires: { ...FRESH_TIRE_HEALTH },
    pitServiceContact: false,
    pitStopVisitCount: 0,
  };
}

function cloneState(state: VehicleState): VehicleState {
  return {
    ...state,
    position: { ...state.position },
    ...(state.tires ? { tires: { ...state.tires } } : {}),
    ...(state.pitServiceContact !== undefined
      ? { pitServiceContact: state.pitServiceContact }
      : {}),
    ...(state.pitStopVisitCount !== undefined
      ? { pitStopVisitCount: state.pitStopVisitCount }
      : {}),
  };
}

function directionForState(state: VehicleState): DriveDirection {
  if (Math.abs(state.longitudinalSpeed) < 0.2) return 0;
  return state.longitudinalSpeed < 0 ? -1 : 1;
}

function normalizeAngle(angle: number): number {
  let normalized = angle;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  while (normalized < -Math.PI) normalized += Math.PI * 2;
  return normalized;
}

function emptyTelemetry(): CarTelemetry {
  return {
    appliedThrottle: 0,
    appliedBrake: 0,
    appliedSteering: 0,
    speedRatio: 0,
    driveDirection: 0,
    tick: 0,
    lateralSpeed: 0,
    yawRate: 0,
    steeringAngleRad: 0,
  };
}
